import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNexOs } from "@/lib/nexOs";
import { useFloatingWindows } from "@/lib/floatingWindows";

const SPEED = 38; // px per second
const TONE = { success: "text-emerald-300", info: "text-sky-300", warning: "text-amber-300", danger: "text-red-300" };
const typeLabel = (t) => String(t || "").split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join("_");
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Newest first, bursts of archive sweeps folded into one entry, capped at 30. */
export function tickerEntries(feed) {
  const out = [];
  for (const ev of feed || []) {
    if (String(ev.id || "").startsWith("empty_")) continue;
    const last = out[out.length - 1];
    if (ev.type === "archive_sweep" && last?.type === "archive_sweep") { last.count += 1; continue; }
    out.push({ ...ev, count: 1 });
  }
  return out.slice(0, 30);
}

function usePrefersReducedMotion() {
  const q = "(prefers-reduced-motion: reduce)";
  const [on, setOn] = useState(() => typeof window !== "undefined" && window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return undefined;
    const h = () => setOn(m.matches);
    m.addEventListener?.("change", h);
    return () => m.removeEventListener?.("change", h);
  }, []);
  return on;
}

function Item({ ev, onOpen, dup }) {
  const ledger = ev.kind === "ledger";
  const tone = TONE[ev.status] || TONE.info;
  const link = ledger && onOpen;
  const Tag = link ? "button" : "span";
  const props = link ? { type: "button", onClick: () => onOpen(ev), title: "Open in Resident Ledger" } : {};
  const sweep = ledger && ev.count > 1;
  return (
    <Tag {...props} tabIndex={dup ? -1 : undefined} aria-hidden={dup || undefined} data-testid={`nds-ticker-item${dup ? "-dup" : ""}-${ev.id}`}
      className={`inline-flex items-center gap-2 shrink-0 px-3 font-mono text-[11px] whitespace-nowrap rounded-sm ${link ? "hover:bg-white/5 focus:outline-none focus-visible:ring-1 focus-visible:ring-nex-gold" : ""}`}>
      {sweep ? <span className="text-nex-silver">Archive sweep · {ev.count} entries</span> : ledger ? <>
        <span className="text-nex-silver uppercase tracking-[0.12em]">{typeLabel(ev.type)}</span>
        <span className={tone}>{ev.direction === "credit" ? "+" : "−"}{usd(ev.amount)}</span>
        <span className="text-nex-muted uppercase tracking-[0.12em]">{String(ev.bucket || "").replace(/_/g, " ")}</span>
        {ev.description && <span className="max-w-[240px] truncate text-nex-silver/80">{ev.description}</span>}
      </> : <>
        <span className={`uppercase tracking-[0.12em] ${tone}`}>{ev.event}</span>
        {ev.details && <span className="max-w-[260px] truncate text-nex-silver/80">{ev.details}</span>}
      </>}
      <span className="text-nex-muted uppercase text-[10px]">{ev.time}</span>
    </Tag>
  );
}

const Sep = () => <span aria-hidden="true" className="shrink-0 text-white/20">•</span>;

/** Right-to-left loop driven by a pixel offset, so new entries slide in at the front without restarting the scroll. */
function Marquee({ items, render }) {
  const box = useRef(null);
  const track = useRef(null);
  const off = useRef(0);
  const paused = useRef(false);
  const prev = useRef([]);
  useLayoutEffect(() => {
    const ids = items.map((x) => x.id);
    const added = prev.current.length ? ids.indexOf(prev.current[0]) : 0;
    if (added > 0 && track.current) {
      const kids = track.current.children;
      for (let k = 0; k < added * 2; k += 1) off.current += kids[k]?.offsetWidth || 0;
    }
    prev.current = ids;
  }, [items]);
  useEffect(() => {
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      const el = track.current;
      if (el && !paused.current) {
        const half = el.scrollWidth / 2;
        off.current += SPEED * dt;
        if (half > 0) off.current %= half;
        el.style.transform = `translateX(${-off.current}px)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  const pause = (v) => () => { paused.current = v; if (box.current) box.current.dataset.paused = String(v); };
  return (
    <div ref={box} data-testid="nds-ticker-marquee" data-paused="false" className="relative flex-1 min-w-0 overflow-hidden"
      onMouseEnter={pause(true)} onMouseLeave={pause(false)} onFocusCapture={pause(true)} onBlurCapture={pause(false)}>
      <div ref={track} className="flex items-center w-max will-change-transform">
        {[...items, ...items].map((ev, i) => <React.Fragment key={`${ev.id}-${i}`}>{render(ev, i >= items.length)}<Sep /></React.Fragment>)}
      </div>
    </div>
  );
}

/** Reduced motion: one entry at a time, swapping every 5 seconds, with previous / next. */
function Stepper({ items, render }) {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((n) => n + 1), 5000); return () => clearInterval(t); }, []);
  const n = items.length;
  const cur = items[((i % n) + n) % n];
  const arrow = "p-1 text-nex-muted hover:text-nex-gold";
  return (
    <div className="flex-1 min-w-0 flex items-center gap-1" data-testid="nds-ticker-stepper">
      <button type="button" onClick={() => setI((x) => x - 1)} aria-label="Previous entry" data-testid="nds-ticker-prev" className={arrow}><ChevronLeft className="h-3.5 w-3.5" /></button>
      <div className="flex-1 min-w-0 overflow-hidden">{render(cur, false)}</div>
      <button type="button" onClick={() => setI((x) => x + 1)} aria-label="Next entry" data-testid="nds-ticker-next" className={arrow}><ChevronRight className="h-3.5 w-3.5" /></button>
    </div>
  );
}

const Muted = ({ children, testid }) => <span data-testid={testid} className="flex-1 min-w-0 font-mono text-[11px] text-nex-muted truncate">{children}</span>;

/** Tier 1 · Live System Feed as a slim full-width ticker (same feed + 30s refresh as before). */
export default function LiveTicker() {
  const { feed, feedOk, finPeriod, currentVertical } = useNexOs();
  const ranged = !!finPeriod && currentVertical?.id === "finance_engine";
  const { openWindow } = useFloatingWindows();
  const reduced = usePrefersReducedMotion();
  const items = useMemo(() => tickerEntries(feed), [feed]);
  const open = (ev) => openWindow("fin_ledger", { title: "Resident Ledger", highlight: { txn_id: ev.txn_id, resident_id: ev.resident_id, created_at: ev.ts, bucket: ev.bucket } });
  const render = (ev, dup) => <Item key={ev.id} ev={ev} dup={dup} onOpen={open} />;
  let body;
  if (feedOk === false || (feedOk === null && !feed.length)) body = <Muted testid="nds-ticker-reconnecting">Reconnecting…</Muted>;
  else if (!items.length) body = <Muted testid="nds-ticker-empty">{ranged ? "No activity in this range" : "No recent ledger activity"}</Muted>;
  else if (reduced) body = <Stepper items={items} render={render} />;
  else if (items.length <= 2) body = <div data-testid="nds-ticker-static" className="flex-1 min-w-0 flex items-center overflow-hidden">{items.map((ev) => <React.Fragment key={ev.id}>{render(ev, false)}<Sep /></React.Fragment>)}</div>;
  else body = <Marquee items={items} render={render} />;
  return (
    <div role="region" aria-label="Live system feed" data-testid="nds-live-ticker" className="nds-card-dark h-10 px-3 flex items-center gap-3 overflow-hidden">
      <div className="flex items-center gap-2 shrink-0 pr-3 border-r border-white/10">
        <span className="nds-live-dot" />
        <span className="font-mono text-[0.65rem] tracking-[0.24em] text-nex-silver uppercase">Live</span>
      </div>
      {body}
    </div>
  );
}
